import React, { useEffect, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { CalendarService } from "./calendarUtils";

interface Props {
  services: CalendarService[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  singleServiceMode?: boolean;
}

export default function CalendarServiceFilter({ services, selectedIds, onChange, singleServiceMode }: Props) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const selected = services.filter((service) => selectedIds.includes(service.serviceId));
  const visible = services.filter((service) => service.name.toLowerCase().includes(search.trim().toLowerCase()));
  const toggle = (serviceId: number) => onChange(
    selectedIds.includes(serviceId)
      ? selectedIds.filter((id) => id !== serviceId)
      : [...selectedIds, serviceId],
  );

  if (singleServiceMode) {
    return selected[0] ? (
      <div className="calendar-single-service" style={{ ["--service-colour" as string]: selected[0].colorHex || "#037d41" }}>
        <span aria-hidden="true" />{selected[0].name}
      </div>
    ) : null;
  }

  return (
    <section className="calendar-filter" aria-label="Filter calendar services">
      <div className="calendar-service-chips" aria-live="polite">
        <span className="calendar-filter-label">Services</span>
        {selected.map((service) => (
          <span key={service.serviceId} className="calendar-service-chip" style={{ ["--service-colour" as string]: service.colorHex || "#037d41" }}>
            {service.name}
            <button type="button" onClick={() => toggle(service.serviceId)} aria-label={`Remove ${service.name}`}>
              <CloseIcon fontSize="inherit" />
            </button>
          </span>
        ))}
      </div>

      <div className="calendar-filter-controls">
        <div className="calendar-filter-actions">
          <button type="button" className="calendar-filter-action calendar-filter-action--primary" onClick={() => onChange(services.map((service) => service.serviceId))}>Select all</button>
          <button type="button" className="calendar-filter-action" onClick={() => onChange([])}>Deselect all</button>
        </div>
        <div className="calendar-service-select" ref={rootRef}>
          <button type="button" className="calendar-service-select-trigger" onClick={() => setOpen((value) => !value)} aria-haspopup="listbox" aria-expanded={open}>
            <span>{selectedIds.length} selected</span><ExpandMoreIcon />
          </button>
          {open && (
            <div className="calendar-service-menu" role="listbox" aria-multiselectable="true">
              {services.length > 7 && <input className="calendar-service-search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Find a service" aria-label="Find a service" autoFocus />}
              {visible.map((service) => (
                <label key={service.serviceId} className="calendar-service-option">
                  <input type="checkbox" checked={selectedIds.includes(service.serviceId)} onChange={() => toggle(service.serviceId)} />
                  <span className="calendar-service-option-dot" style={{ background: service.colorHex || "#037d41" }} />
                  <span>{service.name}</span>
                </label>
              ))}
              {visible.length === 0 && <div className="calendar-service-menu-empty">No matching services</div>}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
